// src/components/sections/hero/HeroSection.tsx
import React, { memo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Container, Typography, Stack, Link } from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useTheme } from '../../../../core/providers/ThemeProvider';
import { containerMaxWidths, responsiveSpacing } from "../../../styles/theme";
import { responsiveFontSize, useResponsive } from "../../../utils/responsive";
import translations from '../../../translations/en.json';
import { SearchForm } from "../../common/SearchForm/SearchForm";
import { Navbar } from "../../layout/Navbar";
import { AuthNavbar } from "../../layout/AuthNavbar";
import StatusMessage from "../../ui/Badge/Statusmessage";
import { useTranslation } from 'react-i18next';

export const HeroSection: React.FC = memo(() => {
  const { userType, theme } = useTheme();
  const { isAuthenticated } = useAuth0();
  const { t } = useTranslation();
  const navigate = useNavigate();
  useResponsive();

  const [blockedError, setBlockedError] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem('auth_blocked_error') === 'true') {
        setBlockedError(true);
        sessionStorage.removeItem('auth_blocked_error');
      }
    } catch {
      // sessionStorage unavailable
    }
  }, []);

  const getBackgroundStyle = () => {
    if (userType === 'sender') {
      return {
        backgroundImage: 'url(/image/background/Sender-background.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }

    if (userType === 'generic') {
      return {
        backgroundImage: 'url(/image/background/genericbg.svg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      };
    }

    const gradient = theme.colors.gradient as string;
    return {
      background: gradient,
    };
  };

  return (
    <Box
      component="section"
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: {
          xs: '500px',
          sm: '600px',
          lg: '682px',
          xl: '720px',
          xxl: '780px',
        },
        pt: {
          xs: responsiveSpacing.containerPadding.xs,
          sm: responsiveSpacing.containerPadding.sm,
          md: responsiveSpacing.containerPadding.md,
          lg: responsiveSpacing.containerPadding.lg,
          xl: responsiveSpacing.containerPadding.xl,
          xxl: responsiveSpacing.containerPadding.xxl,
        },
        px: {
          xs: responsiveSpacing.containerPadding.xs,
          sm: responsiveSpacing.containerPadding.sm,
          md: responsiveSpacing.containerPadding.md,
          lg: responsiveSpacing.containerPadding.lg,
          xl: responsiveSpacing.containerPadding.xl,
          xxl: responsiveSpacing.containerPadding.xxl,
        },
        '@media (max-width: 310px) and (max-height: 300px)': {
          px: '12px',
        },
        pb: {
          xs: responsiveSpacing.sectionPadding.xs,
          sm: responsiveSpacing.sectionPadding.sm,
          md: responsiveSpacing.sectionPadding.md,
          lg: responsiveSpacing.sectionPadding.lg,
          xl: responsiveSpacing.sectionPadding.xl,
          xxl: responsiveSpacing.sectionPadding.xxl,
        },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: {
          xs: responsiveSpacing.mediumGap.xs,
          sm: responsiveSpacing.largeGap.sm,
          md: responsiveSpacing.largeGap.md,
          lg: responsiveSpacing.largeGap.lg,
          xl: responsiveSpacing.largeGap.xl,
          xxl: responsiveSpacing.largeGap.xxl,
        },
        transition: 'all 0.5s ease',
        ...getBackgroundStyle(),
      }}
    >
      {/* Navigation Bar - Fixed Container */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          px: {
            xs: responsiveSpacing.containerPadding.xs,
            sm: responsiveSpacing.containerPadding.sm,
            md: responsiveSpacing.containerPadding.md,
            lg: responsiveSpacing.containerPadding.lg,
            xl: responsiveSpacing.containerPadding.xl,
            xxl: responsiveSpacing.containerPadding.xxl,
          },
          pt: {
            xs: responsiveSpacing.containerPadding.xs,
            sm: responsiveSpacing.containerPadding.sm,
            md: responsiveSpacing.containerPadding.md,
            lg: responsiveSpacing.containerPadding.lg,
            xl: responsiveSpacing.containerPadding.xl,
            xxl: responsiveSpacing.containerPadding.xxl,
          },
          pb: {
            xs: '1rem',
            sm: '1rem',
            md: '1rem',
            lg: '1.25rem',
            xl: '1.25rem',
            xxl: '1.25rem',
          },
          '@media (max-width: 310px) and (max-height: 300px)': {
            px: '12px',
            pt: '12px',
            pb: '8px',
          },
          pointerEvents: 'none',
        }}
      >
        <Container
          maxWidth={false}
          sx={{
            maxWidth: {
              xs: '100%',
              sm: '100%',
              md: '100%',
              lg: '1360px',
              xl: '1440px',
              xxl: '1536px',
            },
            width: '100%',
            padding: '0 !important',
            pointerEvents: 'auto',
          }}
        >
          {isAuthenticated ? <AuthNavbar /> : <Navbar />}
        </Container>
      </Box>

      {/* Hero Content Container */}
      <Container
        maxWidth={false}
        sx={{
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '100%',
            lg: '1024px',
            xl: '1024px',
            xxl: '1120px',
          },
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: {
            xs: '2rem',
            sm: '2rem',
            lg: '2rem',
            xl: '2.5rem',
            xxl: '3rem',
          },
          mt: {
            xs: '8rem',
            sm: '9rem',
            lg: '10rem',
            xl: '10.5rem',
            xxl: '11rem',
          },
          padding: '0 !important',
        }}
      >
        {/* Blocked user error banner */}
        {blockedError && (
          <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
            <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '560px', md: '600px' } }}>
              <StatusMessage
                status="error"
                message={t('auth.userBlocked')}
                messageNode={
                  <span>
                    {t('auth.userBlockedPrefix')}{' '}
                    <Link component="span" onClick={() => navigate('/support', { state: { fromApp: true } })} sx={{ color: 'inherit', fontWeight: 600, cursor: 'pointer' }}>
                      {t('auth.userBlockedLink')}
                    </Link>
                  </span>
                }
                showButton={false}
                showCloseButton={true}
                onClose={() => setBlockedError(false)}
              />
            </Box>
          </Box>
        )}

        {/* Text Content Section */}
        <Stack
          spacing={{
            xs: 1.5,
            sm: 2,
            lg: 3,
            xl: 3.5,
            xxl: 4,
          }}
          alignItems="center"
          sx={{ width: '100%' }}
        >
          {/* Title Badge - "Welcome to SkyPorter" */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, xl: 1, xxl: 1.25 },
            }}
          >
            <Box
              sx={{
                width: { xs: '13px', xl: '15px', xxl: '17px' },
                height: { xs: '2px', xl: '2.5px', xxl: '3px' },
                backgroundColor: 'white',
              }}
            />
            <Typography
              sx={{
                color: 'white',
                fontSize: {
                  xs: '0.75rem',
                  sm: '0.875rem',
                  lg: '1rem',
                  xl: '1.0625rem',
                  xxl: '1.125rem',
                },
                fontWeight: 600,
                fontFamily: theme.typography.bodyText.fontFamily,
                lineHeight: 1.5,
              }}
            >
              {translations.common.title}
            </Typography>
            <Box
              sx={{
                width: { xs: '13px', xl: '15px', xxl: '17px' },
                height: { xs: '2px', xl: '2.5px', xxl: '3px' },
                backgroundColor: 'white',
              }}
            />
          </Box>

          {/* Main Heading with fluid typography */}
          <Box
            sx={{
              width: '100%',
              maxWidth: { xs: '100%', lg: '597px', xl: '680px', xxl: '780px' },
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography
              component="h1"
              sx={{
                color: 'white',
                fontWeight: 700,
                fontFamily: theme.typography.sectionHeading.fontFamily,
                textAlign: 'center',
                fontSize: responsiveFontSize(36, 70, { maxViewport: 1920 }),
                lineHeight: {
                  xs: 1.2,
                  sm: 1.2,
                  lg: 1.3,
                  xl: 1.35,
                  xxl: 1.4,
                },
                letterSpacing: '-0.02em',
              }}
            >
              {userType === "sender" ? translations.header.sender.title : userType === "generic" ? translations.header.generic.title : translations.header.traveller.title}
            </Typography>
          </Box>

          {/* Description with fluid typography */}
          <Typography
            sx={{
              width: '100%',
              maxWidth: {
                xs: '100%',
                md: '100%',
                lg: '1024px',
                xl: '1024px',
                xxl: '1120px',
              },
              color: 'white',
              fontWeight: 400,
              fontFamily: theme.typography.bodyText.fontFamily,
              textAlign: 'center',
              fontSize: responsiveFontSize(14, 20, { capAtReadableSize: true }),
              lineHeight: {
                xs: 1.75,
                sm: 2,
                lg: 1.6,
                xl: 1.65,
                xxl: 1.7,
              },
              px: { xs: 1, sm: 2, md: 2, lg: 0 },
            }}
          >
            {userType === "sender"
              ? translations.header.sender.subtitle
              : userType === "generic"
              ? translations.header.generic.subtitle
              : translations.header.traveller.subtitle}
          </Typography>
        </Stack>

        {/* Search Form - Full width within container */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
          <SearchForm isHomePage={true} />
        </Box>
      </Container>
    </Box>
  );
});

HeroSection.displayName = "HeroSection";